import { useEffect, useRef, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { useGoogleMaps } from '@/hooks/useGoogleMaps';
import { toast } from 'sonner';
import { Pencil, Trash2, Check, X } from 'lucide-react';

interface ManualRoofTraceProps {
  address: string;
  lat: number;
  lng: number;
  onMeasurementComplete: (data: {
    totalAreaSqFt: number;
    totalSquares: number;
    planes: Array<{
      id: string;
      areaSqFt: number;
      vertices: Array<{ lat: number; lng: number }>;
    }>;
  }) => void;
}

export function ManualRoofTrace({ address, lat, lng, onMeasurementComplete }: ManualRoofTraceProps) {
  const mapRef = useRef<HTMLDivElement>(null);
  const mapInstanceRef = useRef<google.maps.Map | null>(null);
  const [isDrawing, setIsDrawing] = useState(false);
  const [currentPolygon, setCurrentPolygon] = useState<google.maps.Polygon | null>(null);
  const [currentPath, setCurrentPath] = useState<google.maps.LatLng[]>([]);
  const [completedPolygons, setCompletedPolygons] = useState<google.maps.Polygon[]>([]);
  const [markers, setMarkers] = useState<google.maps.Marker[]>([]);
  const { isLoaded } = useGoogleMaps();

  // Calculate polygon area in sq ft
  const calculatePolygonArea = (polygon: google.maps.Polygon): number => {
    const path = polygon.getPath();
    const area = google.maps.geometry.spherical.computeArea(path);
    return area * 10.764; // Convert sq meters to sq ft
  };

  // Initialize map
  useEffect(() => {
    if (!isLoaded || !mapRef.current) return;

    const map = new google.maps.Map(mapRef.current, {
      center: { lat, lng },
      zoom: 21,
      mapTypeId: 'satellite',
      tilt: 0,
      disableDefaultUI: true,
      zoomControl: true,
      fullscreenControl: true,
    });

    mapInstanceRef.current = map;

    return () => {
      completedPolygons.forEach(p => p.setMap(null));
      markers.forEach(m => m.setMap(null));
      if (currentPolygon) currentPolygon.setMap(null);
    };
  }, [isLoaded, lat, lng]);

  const startDrawing = () => {
    if (!mapInstanceRef.current) return;
    
    setIsDrawing(true);
    setCurrentPath([]);
    
    const map = mapInstanceRef.current;
    
    // Add click listener to map
    const clickListener = map.addListener('click', (e: google.maps.MapMouseEvent) => {
      if (!e.latLng) return;
      
      const newPath = [...currentPath, e.latLng];
      setCurrentPath(newPath);
      
      // Add marker at click point
      const marker = new google.maps.Marker({
        position: e.latLng,
        map,
        icon: {
          path: google.maps.SymbolPath.CIRCLE,
          scale: 5,
          fillColor: '#3b82f6',
          fillOpacity: 1,
          strokeColor: '#ffffff',
          strokeWeight: 2,
        },
      });
      
      setMarkers(prev => [...prev, marker]);
      
      // Update or create polygon preview
      if (currentPolygon) {
        currentPolygon.setPath(newPath);
      } else {
        const polygon = new google.maps.Polygon({
          paths: newPath,
          strokeColor: '#3b82f6',
          strokeOpacity: 1,
          strokeWeight: 2,
          fillColor: '#3b82f6',
          fillOpacity: 0.2,
          map,
          clickable: false,
        });
        setCurrentPolygon(polygon);
      }
    });
    
    // Store listener to remove later
    (map as any)._clickListener = clickListener;
  };

  const finishDrawing = () => {
    if (!mapInstanceRef.current || !currentPolygon || currentPath.length < 3) {
      toast.error('Need at least 3 points to complete a polygon');
      return;
    }

    // Complete the polygon
    currentPolygon.setOptions({
      strokeColor: '#22c55e',
      fillColor: '#22c55e',
      fillOpacity: 0.3,
      clickable: true,
    });

    // Add area label
    const areaSqFt = calculatePolygonArea(currentPolygon);
    const bounds = new google.maps.LatLngBounds();
    currentPath.forEach(p => bounds.extend(p));
    const center = bounds.getCenter();

    const label = new google.maps.Marker({
      position: center,
      map: mapInstanceRef.current,
      icon: {
        path: google.maps.SymbolPath.CIRCLE,
        scale: 0,
      },
      label: {
        text: `${Math.round(areaSqFt)} sq ft`,
        color: '#ffffff',
        fontSize: '12px',
        fontWeight: 'bold',
      },
    });

    setMarkers(prev => [...prev, label]);
    setCompletedPolygons(prev => [...prev, currentPolygon]);
    
    // Cleanup
    setIsDrawing(false);
    setCurrentPolygon(null);
    setCurrentPath([]);
    
    // Remove click listener
    if ((mapInstanceRef.current as any)._clickListener) {
      google.maps.event.removeListener((mapInstanceRef.current as any)._clickListener);
    }
    
    toast.success('Polygon completed');
  };

  const cancelDrawing = () => {
    if (currentPolygon) {
      currentPolygon.setMap(null);
    }
    markers.forEach(m => m.setMap(null));
    setMarkers([]);
    setCurrentPolygon(null);
    setCurrentPath([]);
    setIsDrawing(false);
    
    if (mapInstanceRef.current && (mapInstanceRef.current as any)._clickListener) {
      google.maps.event.removeListener((mapInstanceRef.current as any)._clickListener);
    }
  };

  const clearAll = () => {
    completedPolygons.forEach(p => p.setMap(null));
    markers.forEach(m => m.setMap(null));
    if (currentPolygon) currentPolygon.setMap(null);
    
    setCompletedPolygons([]);
    setMarkers([]);
    setCurrentPolygon(null);
    setCurrentPath([]);
    setIsDrawing(false);
    
    toast.success('All polygons cleared');
  };

  const saveMeasurement = () => {
    if (completedPolygons.length === 0) {
      toast.error('Draw at least one polygon first');
      return;
    }

    const planes = completedPolygons.map((polygon, index) => {
      const path = polygon.getPath();
      const vertices = [];
      for (let i = 0; i < path.getLength(); i++) {
        const point = path.getAt(i);
        vertices.push({ lat: point.lat(), lng: point.lng() });
      }
      
      return {
        id: `manual-plane-${index + 1}`,
        areaSqFt: calculatePolygonArea(polygon),
        vertices,
      };
    });

    const totalAreaSqFt = planes.reduce((sum, p) => sum + p.areaSqFt, 0);
    const totalSquares = totalAreaSqFt / 100;

    onMeasurementComplete({
      totalAreaSqFt,
      totalSquares,
      planes,
    });

    toast.success('Manual measurement saved!');
  };

  if (!isLoaded) {
    return (
      <Card>
        <CardContent className="pt-12 pb-12 text-center">
          <p className="text-muted-foreground">Loading map...</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center justify-between">
          <span className="flex items-center gap-2">
            <Pencil className="w-5 h-5" />
            Manual Roof Trace
          </span>
          <div className="flex gap-2">
            {!isDrawing && completedPolygons.length > 0 && (
              <>
                <Button variant="outline" size="sm" onClick={clearAll}>
                  <Trash2 className="w-4 h-4 mr-2" />
                  Clear All
                </Button>
                <Button variant="default" size="sm" onClick={saveMeasurement}>
                  <Check className="w-4 h-4 mr-2" />
                  Save ({completedPolygons.length} planes)
                </Button>
              </>
            )}
            {!isDrawing && completedPolygons.length === 0 && (
              <Button variant="default" size="sm" onClick={startDrawing}>
                <Pencil className="w-4 h-4 mr-2" />
                Start Drawing
              </Button>
            )}
            {isDrawing && (
              <>
                <Button variant="outline" size="sm" onClick={cancelDrawing}>
                  <X className="w-4 h-4 mr-2" />
                  Cancel
                </Button>
                <Button variant="default" size="sm" onClick={finishDrawing} disabled={currentPath.length < 3}>
                  <Check className="w-4 h-4 mr-2" />
                  Finish Polygon
                </Button>
              </>
            )}
          </div>
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div 
          ref={mapRef} 
          className="w-full h-[600px] rounded-lg border border-border"
        />
        {isDrawing && (
          <p className="text-sm text-muted-foreground mt-4">
            Click on the map to add points. Need at least 3 points to complete a polygon.
            Current points: {currentPath.length}
          </p>
        )}
        {!isDrawing && completedPolygons.length > 0 && (
          <p className="text-sm text-muted-foreground mt-4">
            {completedPolygons.length} plane(s) drawn. 
            Total: {Math.round(completedPolygons.reduce((sum, p) => sum + calculatePolygonArea(p), 0))} sq ft
          </p>
        )}
      </CardContent>
    </Card>
  );
}
